/**
 * Browser-Pre-Upload-Klassifikator für PDF-Pläne. Lädt die Datei mit pdfjs-dist
 * im Browser, zählt pro Seite die `constructPath`/`paintImageXObject`-Operatoren
 * und liest OCG-Layer + Plan-Text. Aus den Counts baut shared/pdf-classify-page
 * den Vor-Upload-Report (Single Source of Truth — gleiche Schwellwerte wie der
 * pdf-classifier-agent im Backend).
 */

import * as pdfjsLib from "pdfjs-dist";
// `?worker` statt `?url` — Vite bündelt den Worker als eigenes Chunk und liefert
// eine Worker-Konstruktor-Klasse zurück. Mit `?url` versuchte pdfjs intern eine
// ESM-Auflösung im Worker-Bootstrap und scheiterte an einer von Vite optimierten
// deps-URL (sichtbar als 404 auf `pdfjs-dist_build_pdf__worker__min.mjs`).
import PdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";
import {
  buildPreUploadReport,
  extractScaleHintFromText,
  type PreUploadReport,
} from "@bidi-takeoff/shared/pdf-classify-page";

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

interface PdfjsOpsModule {
  constructPath: number;
  paintImageXObject: number;
  paintImageXObjectGroup: number;
  paintImageMaskXObject: number;
}

export async function classifyPdfInBrowser(
  file: File
): Promise<PreUploadReport> {
  const arrayBuffer = await file.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);

  const loadingTask = pdfjsLib.getDocument({ data });
  const doc = await loadingTask.promise;
  try {
    const ocgLayers = await readOcg(doc);

    // Operator-Counts pro Seite + gesamten Plan-Text für Maßstabs-Regex.
    const pageStats: Array<{
      index: number;
      pathConstructCount: number;
      imageStreamCount: number;
      pageWidth: number;
      pageHeight: number;
    }> = [];
    let combinedText = "";
    const OPS = pdfjsLib.OPS as unknown as PdfjsOpsModule;

    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const opList = await page.getOperatorList();
      const counts = countOps(opList.fnArray, OPS);
      pageStats.push({
        index: i - 1,
        pathConstructCount: counts.constructPath,
        imageStreamCount: counts.imageXObject,
        pageWidth: viewport.width,
        pageHeight: viewport.height,
      });
      try {
        const tc = await page.getTextContent();
        for (const item of tc.items) {
          if (
            typeof item === "object" &&
            item !== null &&
            "str" in item &&
            typeof (item as { str: unknown }).str === "string"
          ) {
            combinedText += " " + (item as { str: string }).str;
          }
        }
      } catch {
        // text-content nicht abrufbar — kein Maßstabs-Hint von dieser Seite
      }
    }

    const scaleHint = extractScaleHintFromText(combinedText);

    return buildPreUploadReport({
      filename: file.name,
      pageStats,
      hasOcgLayers: ocgLayers.length > 0,
      ocgLayerCount: ocgLayers.length,
      scaleHint,
    });
  } finally {
    await doc.destroy();
  }
}

interface PdfjsOcgGroup {
  name?: string;
}
interface PdfjsOcgConfig {
  getGroups?: () => Record<string, PdfjsOcgGroup>;
}

async function readOcg(
  doc: pdfjsLib.PDFDocumentProxy
): Promise<Array<{ id: string; name: string }>> {
  try {
    const cfg = (await doc.getOptionalContentConfig()) as PdfjsOcgConfig | null;
    if (!cfg) return [];
    const groups = cfg.getGroups?.() ?? {};
    return Object.entries(groups).map(([id, g]) => ({
      id,
      name: g.name ?? "",
    }));
  } catch {
    return [];
  }
}

function countOps(
  fnArray: number[],
  OPS: PdfjsOpsModule
): { constructPath: number; imageXObject: number } {
  let constructPath = 0;
  let imageXObject = 0;
  for (const fn of fnArray) {
    if (fn === OPS.constructPath) constructPath++;
    else if (
      fn === OPS.paintImageXObject ||
      fn === OPS.paintImageXObjectGroup ||
      fn === OPS.paintImageMaskXObject
    ) {
      imageXObject++;
    }
  }
  return { constructPath, imageXObject };
}
